import { Loader } from '@plaidev/sour-react';
import { Link } from '@tanstack/react-router';
import type { ReactElement } from 'react';
import { DomainV2 } from 'troppus-common/domain';

import { TaskResultBadge } from '../TaskResultBadge';
import { humanizeColumnChangeReason, humanizeRevisionOtherChange } from './formatters';
import type { ImportTaskDetail } from './types';

import ImportTableDefinition = DomainV2.Import.ImportTableDefinition;

type BasicInfoSectionProps = {
  task: ImportTaskDetail;
  tableDefinition: { id: ImportTableDefinition.Id; displayName: string } | undefined;
  tableDefinitionFallbackText: string;
  /** マップ構築中で表示名がまだ解決できないとき true（一覧と同様に ID を出さない） */
  tableDefinitionLabelLoading?: boolean;
  creatorName: string;
  isSystemAdmin: boolean;
};

type RevisionSummary = NonNullable<ImportTaskDetail['importTableDefinitionRevision']>;

type InfoItemProps = {
  label: string;
  value: ReactElement | string;
  className?: string;
};

function InfoItem({ label, value, className }: InfoItemProps): ReactElement {
  return (
    <div className={className}>
      <dt className="text-xs font-medium text-muted-light">{label}</dt>
      <dd className="mt-1 min-w-0 text-sm text-default">{value}</dd>
    </div>
  );
}

function compatibilityLabel(type: RevisionSummary['compatibilityType']): string {
  if (type === 'initial') return '初期 version';
  if (type === 'compatible') return '互換 version';
  return '要確認 version';
}

function compatibilityBadgeClass(type: RevisionSummary['compatibilityType']): string {
  if (type === 'initial') return 'bg-subtle text-muted-light';
  if (type === 'compatible') return 'bg-primary-light text-primary';
  return 'bg-negative-light text-negative';
}

function hasChangeSummary(summary: RevisionSummary['changeSummary']): boolean {
  return Boolean(
    (summary?.addedColumns && summary.addedColumns.length > 0) ||
      (summary?.removedColumns && summary.removedColumns.length > 0) ||
      (summary?.changedColumns && summary.changedColumns.length > 0) ||
      (summary?.otherChanges && summary.otherChanges.length > 0),
  );
}

function RevisionChangeSummaryView({ summary }: { summary: RevisionSummary['changeSummary'] }): ReactElement | null {
  if (!hasChangeSummary(summary)) return null;

  return (
    <div className="mt-2 flex flex-col gap-2 text-xs">
      {summary?.addedColumns && summary.addedColumns.length > 0 ? (
        <p>
          <span className="text-muted-light">追加された列：</span>
          <span>{summary.addedColumns.join('、')}</span>
        </p>
      ) : null}
      {summary?.removedColumns && summary.removedColumns.length > 0 ? (
        <p>
          <span className="text-muted-light">削除された列：</span>
          <span>{summary.removedColumns.join('、')}</span>
        </p>
      ) : null}
      {summary?.changedColumns && summary.changedColumns.length > 0 ? (
        <div>
          <p className="text-muted-light">変更された列</p>
          <ul className="mt-1 list-disc space-y-1 pl-5 text-default">
            {summary.changedColumns.map((column) => (
              <li key={column.columnName}>
                <span className="font-medium">{column.columnName}</span>
                {column.reasons.length > 0 ? (
                  <span className="text-muted-light">（{column.reasons.map(humanizeColumnChangeReason).join('、')}）</span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {summary?.otherChanges && summary.otherChanges.length > 0 ? (
        <div>
          <p className="text-muted-light">その他の設定変更</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-5 text-default">
            {summary.otherChanges.map((line) => (
              <li key={line}>{humanizeRevisionOtherChange(line)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function BasicInfoSection({
  task,
  tableDefinition,
  tableDefinitionFallbackText,
  tableDefinitionLabelLoading,
  creatorName,
  isSystemAdmin,
}: BasicInfoSectionProps): ReactElement {
  const tableDefinitionValue = tableDefinition ? (
    <Link
      to="/table-definitions/$id"
      params={{ id: tableDefinition.id }}
      className="font-medium text-primary underline-offset-2 hover:underline"
    >
      {tableDefinition.displayName}
    </Link>
  ) : tableDefinitionLabelLoading ? (
    <span className="inline-flex items-center gap-2 text-muted-light">
      <Loader />
      <span className="text-sm">読み込み中…</span>
    </span>
  ) : (
    <span className="font-medium">{tableDefinitionFallbackText}</span>
  );
  const revision = task.importTableDefinitionRevision;
  const showSchemaRevisionSummary =
    revision &&
    revision.revisionNumber > 1 &&
    (hasChangeSummary(revision.changeSummary) || revision.compatibilityType === 'possiblyBreaking');
  const tableDefinitionWithVersion = (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        {tableDefinitionValue}
        {revision ? <span className="text-muted-light">/ version {revision.revisionNumber}</span> : null}
        {revision && revision.revisionNumber > 1 ? (
          <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${compatibilityBadgeClass(revision.compatibilityType)}`}
          >
            {compatibilityLabel(revision.compatibilityType)}
          </span>
        ) : null}
      </div>
      {revision && showSchemaRevisionSummary ? (
        <div className="mt-2 rounded-md border border-primary bg-primary-light p-3 text-sm text-default">
          <p className="font-medium text-primary">スキーマ変更のサマリー（version {revision.revisionNumber}）</p>
          <p className="mt-1 text-xs text-muted-light">直前の version からの差分です。</p>
          {hasChangeSummary(revision.changeSummary) ? (
            <RevisionChangeSummaryView summary={revision.changeSummary} />
          ) : (
            <p className="mt-2 text-xs text-default">
              保存された差分の詳細がありません。テーブル定義画面のリビジョン履歴で内容を確認できます。
            </p>
          )}
          <p className="mt-2 text-xs text-muted-light">この version で取り込みタスクを開始しました。</p>
        </div>
      ) : null}
    </div>
  );
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <h2 className="m-0 text-base font-semibold text-default">基本情報</h2>
      </div>
      <div className="rounded-xl border border-default bg-surface">
        <dl className="grid gap-x-6 gap-y-5 px-5 py-5 sm:grid-cols-2 lg:grid-cols-4">
          <InfoItem className="sm:col-span-2" label="テーブル定義" value={tableDefinitionWithVersion} />
          <InfoItem label="結果" value={<TaskResultBadge task={task} />} />
          <InfoItem label="作業者" value={creatorName} />
          <InfoItem label="開始日時" value={task.startedAt ? task.startedAt.format('YYYY-MM-DD HH:mm:ss') : '-'} />
          <InfoItem label="完了日時" value={task.finishedAt ? task.finishedAt.format('YYYY-MM-DD HH:mm:ss') : '-'} />
          {isSystemAdmin ? (
            <InfoItem
              className="admin-ui sm:col-span-2 lg:col-span-4"
              label="タスクID"
              value={<span className="block break-all font-mono text-xs">{task.id}</span>}
            />
          ) : null}
        </dl>
      </div>
    </section>
  );
}
